import constants from '@/Constants';
import { EventStatusType } from '@/modules/core/domain/interfaces';
import { getSubdomain } from '@/utils/getSubdomain';
import { createContext } from '@/utils/react-utils';
import snakeToCamel from '@/utils/snakeToCamel';
import axios from 'axios';
import React from 'react';
import { useQuery } from 'react-query';

import { EventForm } from '../interfaces';

interface EventSummary extends Record<string, unknown> {
  id: string;
  authorName: string;
  localName: string;
  duration: string;
  executionDate: string;
  executionTime: string;
  parentableName: string;
  parentableId: string;
  collectedItemsCount: number;
  status: EventStatusType;
  lastModifiedAt: number;
  finishedAt: number;
  validated?: boolean;
  userProfileName: string;
  items: EventForm[];
}

type SummaryProps = {
  eventId: string;
};

export type EventSummaryProps = {
  event?: EventSummary;
  isLoading: boolean;
};

const [Context, useEventSummary] = createContext<EventSummaryProps>({
  name: 'EventSummaryContext',
});

const EventSummaryProvider: React.FC<SummaryProps> = (props) => {
  const { eventId, children } = props;

  const subdomainFromHost = getSubdomain();

  const url = constants.REPORTS_API_URL || '';
  const parsedUrl = url
    .toLowerCase()
    .replace('{{workspace}}', subdomainFromHost);

  const fetchEventSummary = async () => {
    const { data } = await axios.get<EventSummary>(
      `${parsedUrl}external_events?event_id=${eventId}`,
      {
        headers: {
          Authorization: constants.REPORTS_API_AUTHORIZATION_TOKEN,
        },
      },
    );
    return snakeToCamel(data);
  };

  const { data, isLoading } = useQuery<EventSummary>(
    ['eventSummary', eventId],
    fetchEventSummary,
    {
      refetchOnWindowFocus: false,
    },
  );

  return (
    <Context
      value={{
        event: data,
        isLoading,
      }}
    >
      {children}
    </Context>
  );
};

export { useEventSummary, EventSummaryProvider };
